import { Fragment, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useGetAllMatchesByDateStatusQuery } from '../../../../slices/matchApiSlice';
import MatchScheduleView from './components/match-schedule-view';
import { MatchScheduleModel } from '../../../../model/match-schedule.model';
import Pagination from '../../../../components/pagination';
import { LoadingSceletonSchedule } from '../../../tournaments/routes/all/loading-sceleton-schedule';
import { dateToLocal } from '../../../../utils/dateFormatter';
import {
  DateStatus,
  MatchStatus,
  SeasonStageTypes,
  SkillSyncRegistrationWindow,
} from '../../../../utils/constants';
import { DropdownMenuFilter } from '../../../../components/ui/dropdown-menu/DropdownMenuFIlter';
import { useGetAllSeasonsQuery } from '../../../../slices/seasonApiSlice';
import SkillSyncWeekScheduleView from './components/skill-sync-week-schedule-view';
import { SeasonWeekModel } from '../../../../model/season-week.model';
import HowlingWolf from '../../../../components/howling-wolf';

const MatchScheduleByDate = ({ dateStatus }: { dateStatus: number }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [dataGroupedByDate, setDataGroupedByDate] = useState<{ date: string; data: any }[]>([
    { date: '', data: [] },
  ]);
  const [selectedSeasons, setSelectedSeasons] = useState<string[]>([]);

  const location = window.location.pathname;
  const disablePagination = location.includes('dashboard');
  const {
    data: event,
    isError,
    isSuccess,
    isLoading,
  } = useGetAllMatchesByDateStatusQuery([
    {
      name: searchParams.get('name') ? searchParams.get('name') : '',
      date: searchParams.get('date') ? searchParams.get('date') : '',
      page: searchParams.get('page') ? searchParams.get('page') : 1,
      disablePagination,
    },
    { dateStatus: dateStatus ?? undefined },
  ]);
  const { data: seasonData, isLoading: seasonIsLoading } = useGetAllSeasonsQuery({
    query: '',
    param: '',
    page: 1,
    showMy: true,
    noPagination: true,
  });

  const handleSeasonFilterChange = (games: string[]) => {
    setSelectedSeasons(games);
  };

  useEffect(() => {
    if (event && event.data) {
      const groups = event.data.reduce((groups: any, game: any) => {
        if (selectedSeasons.length && !selectedSeasons.includes(game?.SeasonWeek?.Season?.name)) {
          return groups;
        }
        const date = dateToLocal(game?.match_date)?.split('T')[0];
        if (!groups[date]) {
          groups[date] = [];
        }
        groups[date].push({ ...game, isSkillSyncSeasonWeek: false });
        return groups;
      }, {});

      if (seasonData?.data) {
        seasonData.data.forEach((season: any) => {
          season.SeasonStage?.forEach((stage: any) => {
            if (stage.stage_type_id === SeasonStageTypes['SkillSync'].id) {
              stage.SeasonWeek?.forEach((week: any) => {
                const weekDate = dateToLocal(week.start_time)?.split('T')[0];
                const searchDate = searchParams.get('date');
                if (new Date(week.start_time) >= new Date()) {
                  if (!searchDate || weekDate === searchDate) {
                    if (!groups[weekDate]) {
                      groups[weekDate] = [];
                    }
                    groups[weekDate].push({ ...week, isSkillSyncSeasonWeek: true, Season: season });
                  }
                }
              });
            }
          });
        });
      }
      const groupArrays = Object.keys(groups)?.map((date) => {
        return {
          date,
          data: groups[date].sort((a: any, b: any) => {
            const dateA = new Date(
              a.isSkillSyncSeasonWeek
                ? new Date(a.start_time).getTime() - SkillSyncRegistrationWindow.timeInMilliseconds
                : a.match_date,
            );
            const dateB = new Date(
              b.isSkillSyncSeasonWeek
                ? new Date(b.start_time).getTime() - SkillSyncRegistrationWindow.timeInMilliseconds
                : b.match_date,
            );
            return dateA.getTime() - dateB.getTime();
          }),
        };
      });
      setDataGroupedByDate((prevData) =>
        groupArrays.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
      );
    }
  }, [event, event?.data, setDataGroupedByDate, selectedSeasons]);

  if (isLoading) return <LoadingSceletonSchedule />;
  return (
    <div className='container flex flex-col justify-between flex-1 w-full h-full mx-auto'>
      <div>
        <DropdownMenuFilter
          title='Seasons'
          items={
            seasonData?.data?.map((season: any, index: number) => ({
              label: season.name,
              defaultChecked: selectedSeasons.includes(season.name),
            })) ?? []
          }
          onClear={() => {
            setSelectedSeasons([]);
          }}
          onChange={(checkedItems) => {
            const seasons = checkedItems
              .filter((item) => item.defaultChecked)
              .map((item) => item.label);
            handleSeasonFilterChange(seasons);
          }}
        />
      </div>
      <div className='flex flex-col w-full'>
        {dateStatus === DateStatus.UcomingAndLive && (
          <Fragment>
            <h1 className='my-2 font-bold text-xl'>Ongoing Matches</h1>
            {Object.values(dataGroupedByDate)
              .flatMap((group: any) => group.data)
              .filter((match: any) => match.status === MatchStatus.InProgress).length > 0 ? (
              Object.entries(dataGroupedByDate).map(([key, e]: [string, any], i: number) => (
                <Fragment key={key}>
                  {e.data.filter((g: any) => g.status === MatchStatus.InProgress).length > 0 && (
                    <div>
                      <div className='p-4 font-bold'>
                        {dateToLocal(e?.date, 'ddd MMM DD, YYYY')}
                      </div>
                      <div className='flex flex-col gap-2 '>
                        {e?.data
                          ?.filter((dataRow: any) => dataRow.status === MatchStatus.InProgress)
                          .map((d: any) => {
                            return d.isSkillSyncSeasonWeek ? (
                              <SkillSyncWeekScheduleView
                                key={d.id}
                                seasonWeekModel={new SeasonWeekModel(d)}
                              />
                            ) : (
                              <MatchScheduleView
                                key={d.id}
                                scheduleModel={new MatchScheduleModel(d)}
                              />
                            );
                          })}
                      </div>
                    </div>
                  )}
                </Fragment>
              ))
            ) : (
              <div>
                <h2 className='flex justify-center mb-2'>
                  All your teams have played their matches this week. Good job!
                </h2>
                <HowlingWolf />
              </div>
            )}
            <h1 className='mt-4 font-bold text-xl'>Upcoming Matches</h1>
          </Fragment>
        )}
        {dataGroupedByDate?.map((e: any, i: number) => (
          <Fragment>
            {e.data.filter(
              (g: any) =>
                g.status !== MatchStatus.InProgress &&
                !(dateStatus === 1 && g.isSkillSyncSeasonWeek),
            ).length > 0 && (
              <div key={i}>
                <div className='p-4 font-bold'>{dateToLocal(e?.date, 'ddd MMM DD, YYYY')}</div>
                <div className='flex flex-col gap-2 '>
                  {e?.data
                    ?.filter((dataRow: any) => dataRow.status !== MatchStatus.InProgress)
                    .map((d: any, i: number) => {
                      return d.isSkillSyncSeasonWeek ? (
                        <SkillSyncWeekScheduleView
                          key={d.id}
                          seasonWeekModel={new SeasonWeekModel(d)}
                        />
                      ) : (
                        <MatchScheduleView key={d.id} scheduleModel={new MatchScheduleModel(d)} />
                      );
                    })}
                </div>
              </div>
            )}
          </Fragment>
        ))}
      </div>
      {!disablePagination && <Pagination length={event?.data?.length} {...event?.meta} />}
    </div>
  );
};

export default MatchScheduleByDate;
